import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getPagesByWorkbookId,
  getPageContentById,
  createPageContent,
  updatePageContent,
  deletePageContent,
  usePageContentGeneration,
  CreatePageContentInput,
  UpdatePageContentInput,
  PageContent
} from './page-content-operations';
import { useAIOperationWrapper } from '@/hooks/ai-usage/useAIOperationWrapper';
import { useToast } from '@/hooks/errors/use-toast';
import { syncPageCreated, syncPageDeleted, syncPageUpdated } from '@/utils/project-metadata-sync';
import { useUser } from '@/hooks/auth/use-user';

// Query keys
export const pageContentKeys = {
  all: ['workbook-page-content'] as const,
  byWorkbook: (workbookId: string) => [...pageContentKeys.all, 'workbook', workbookId] as const,
  detail: (id: string) => [...pageContentKeys.all, 'detail', id] as const,
};

/**
 * Hook to fetch all pages for a workbook
 */
export function usePageContentQuery(workbookId: string) {
  return useQuery({
    queryKey: pageContentKeys.byWorkbook(workbookId),
    queryFn: () => getPagesByWorkbookId(workbookId),
    enabled: !!workbookId,
  });
}

/**
 * Hook to fetch a single page by ID
 */
export function usePageContentDetailQuery(id: string) {
  return useQuery({
    queryKey: pageContentKeys.detail(id),
    queryFn: () => getPageContentById(id),
    enabled: !!id,
  });
}

/**
 * Hook for page content mutations with credit checking
 */
export function usePageContentMutations(workbookId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { executeAIOperation } = useAIOperationWrapper();
  const { createPageContentWithWrapper } = usePageContentGeneration();
  const { user } = useUser(); // For AI context cache sync

  const createPageMutation = useMutation({
    mutationFn: (input: CreatePageContentInput) => {
      // Use the wrapper-based approach for credit checking
      return createPageContentWithWrapper(input, executeAIOperation);
    },
    onSuccess: (newPage) => {
      // SCALABLE: Invalidate workbook-specific queries for both owners AND editors
      queryClient.invalidateQueries({ queryKey: pageContentKeys.byWorkbook(workbookId) });
      // CRITICAL: Also invalidate the project navigation query used by tabs
      queryClient.invalidateQueries({ queryKey: ['project-navigation', workbookId] });
      
      // 🤖 AI CONTEXT: Sync to navigation cache for smart detection
      if (user?.id && newPage) {
        syncPageCreated(workbookId, {
          id: newPage.id,
          title: newPage.title,
          type: newPage.type,
          content: newPage.content,
          writing_style: newPage.writing_style
        }, user.id);
      }
      
      toast({
        title: "Success",
        description: "Page created successfully",
      });
    },
    onError: (error) => {
      console.error('Error creating page:', error);
      toast({
        title: "Error",
        description: "Failed to create page",
        variant: "destructive",
      });
    },
  });

  const createPageLegacyMutation = useMutation({
    mutationFn: (input: CreatePageContentInput) => {
      // Legacy direct call (will be deprecated)
      console.warn('[DEPRECATED] Using legacy createPageContent without credit checking');
      return createPageContent(input);
    },
    onSuccess: (newPage) => {
      queryClient.invalidateQueries({ queryKey: pageContentKeys.byWorkbook(workbookId) });
      queryClient.invalidateQueries({ queryKey: ['project-navigation', workbookId] });
      
      // 🤖 AI CONTEXT: Sync to navigation cache for smart detection (legacy)
      if (user?.id && newPage) {
        syncPageCreated(workbookId, {
          id: newPage.id,
          title: newPage.title,
          type: newPage.type,
          content: newPage.content,
          writing_style: newPage.writing_style
        }, user.id);
      }
      
      toast({
        title: "Success",
        description: "Page created successfully",
      });
    },
    onError: (error) => {
      console.error('Error creating page:', error);
      toast({
        title: "Error",
        description: "Failed to create page",
        variant: "destructive",
      });
    },
  });

  const updatePageMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdatePageContentInput }) => 
      updatePageContent(id, updates),
    onSuccess: (updatedPage, { id, updates }) => {
      // SCALABLE: Invalidate workbook-specific queries for both owners AND editors
      queryClient.invalidateQueries({ queryKey: pageContentKeys.byWorkbook(workbookId) });
      queryClient.invalidateQueries({ queryKey: pageContentKeys.detail(updatedPage.id) });
      // Also invalidate project navigation for title changes
      queryClient.invalidateQueries({ queryKey: ['project-navigation', workbookId] });
      
      // 🤖 AI CONTEXT: Sync updates to navigation cache (only meaningful changes)
      if (user?.id && (updates.title || updates.content !== undefined || updates.writing_style)) {
        syncPageUpdated(workbookId, {
          id: updatedPage.id,
          title: updates.title,
          type: updates.type,
          content: updates.content,
          writing_style: updates.writing_style
        }, user.id);
      }
      
      // Don't show toast for every update to avoid spam during typing
    },
    onError: (error) => {
      console.error('Error updating page:', error);
      toast({
        title: "Error",
        description: "Failed to update page",
        variant: "destructive",
      });
    },
  });

  const deletePageMutation = useMutation({
    mutationFn: deletePageContent,
    onSuccess: (_, deletedId) => {
      // SCALABLE: Invalidate workbook-specific queries for both owners AND editors
      queryClient.invalidateQueries({ queryKey: pageContentKeys.byWorkbook(workbookId) });
      queryClient.removeQueries({ queryKey: pageContentKeys.detail(deletedId) });
      // Also invalidate project navigation to update tabs
      queryClient.invalidateQueries({ queryKey: ['project-navigation', workbookId] });
      
      // 🤖 AI CONTEXT: Remove from navigation cache
      if (user?.id) {
        syncPageDeleted(workbookId, deletedId, user.id);
      }
      
      toast({
        title: "Success",
        description: "Page deleted successfully",
      });
    },
    onError: (error) => {
      console.error('Error deleting page:', error);
      toast({
        title: "Error",
        description: "Failed to delete page",
        variant: "destructive",
      });
    },
  });

  return {
    createPage: createPageMutation.mutate,
    createPageAsync: createPageMutation.mutateAsync,
    createPageLegacy: createPageLegacyMutation.mutate, // For backward compatibility
    createPageLegacyAsync: createPageLegacyMutation.mutateAsync, // For backward compatibility
    updatePage: updatePageMutation.mutate,
    updatePageAsync: updatePageMutation.mutateAsync,
    deletePage: deletePageMutation.mutate,
    deletePageAsync: deletePageMutation.mutateAsync,
    isPending: 
      createPageMutation.isPending || 
      createPageLegacyMutation.isPending ||
      updatePageMutation.isPending || 
      deletePageMutation.isPending,
  };
} 
